/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import { DirectiveOptions } from 'vue';
import { DirectiveBinding } from 'vue/types/options';
import { ImageRequest } from '@/directives/ImageService/ImageRequest';


/**
 * Directive to dynamically change img tags into responsive image tags via the
 * Amazon Serverless Image Handler
 *
 * You must pass this Directive an (ImageServiceConfiguration | string)[]
 *
 * @author nchapman@frontier.co.uk
 */
export const imageService: DirectiveOptions = {

    /**
     * Called when the bound element has been inserted into its parent node
     * (this only guarantees parent node presence, not necessarily in-document).
     */
    inserted: (el: HTMLElement, binding: DirectiveBinding): void => {

        const bucketName: string = process.env.VUE_APP_IMG_BUCKET || 'frontier-cms';

        let src: string | null = el.getAttribute('src');

        if (el.nodeName !== 'IMG' || null === src) {
            // @todo Use logLevel to inform of error
            return;
        }

        src = src.replace(new RegExp('https?://([a-zA-Z0-9][-a-zA-Z0-9]*[a-zA-Z0-9]\\.)+([a-zA-Z0-9]{3,5})/'), '');

        const options: (ImageServiceConfiguration | number)[] = binding.value;

        const srcArr: string[] = createAwsSihSrcArr(options, bucketName, src);

        if (srcArr.length > 1) {
            // Create srcset attribute
            if (el) {
                el.setAttribute('srcset', srcArr.join(', '));
            }
            return;
        }

        if (srcArr.length === 1) {
            if (el) {
                el.setAttribute('src', srcArr[0].substr(0, srcArr[0].lastIndexOf(' ')));
            }
            return;
        }

        // @todo Log an error
    },
};


/**
 * Create array of AWS SIH urls
 *
 * @param options
 * @param bucketName
 * @param src
 */
const createAwsSihSrcArr: (
    options: (ImageServiceConfiguration | number)[],
    bucketName: string,
    src: string,
) => string[] = (
    options: (ImageServiceConfiguration | number)[],
    bucketName: string,
    src: string,
): string[] => {
    const srcArr: string[] = [];

    for (const option of options) {

        const imageRequest: ImageRequest = new ImageRequest(bucketName, src);
        let size: string = '';

        if (typeof option === 'object') {
            imageRequest.edits = option.edits;
            size = option.size;
        }

        if (typeof option === 'number') {
            imageRequest.edits = {
                resize: {
                    width: option,
                },
            };

            size = option.toString();
        }

        srcArr.push(`${imageRequest.getURl()} ${(/^\d+$/.test(size)) ? size + 'w' : size}`);
    }

    return srcArr;
};
